import {
  MANAGER_ROLES,
  MY_DAY_RANGE_MAX_DAYS,
  describeRanges,
  describeWeekly,
  effectiveRanges,
  temperatureOf,
  type MyDayTask,
} from '@performance-crm/shared';
import { AlertTriangle, CalendarDays, Clock, Flame, Inbox, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CompleteTaskDialog,
  ConfirmAppointmentDialog,
  RescheduleDialog,
  type AttendanceOutcome,
} from '@/components/leads/dialogs';
import { TemperatureBadge } from '@/components/leads/badges';
import { NoContactBadge, QuickContactButtons } from '@/components/leads/QuickContactButtons';
import { TaskRow } from '@/components/leads/TaskRow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { fmt, todayInTz } from '@/lib/format';
import { useAdvisors, useMyDay, useMySchedules } from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

export function MyDayPage() {
  const { user } = useAuth();
  const isManager = !!user?.memberships.some((m) => MANAGER_ROLES.includes(m.role));
  // El asesor elegido y el rango del calendario viven en la URL (ux-mi-dia-18): al entrar a una ficha y volver,
  // Mi día sigue donde estaba.
  const [params, setParams] = useSearchParams();
  const advisorId = params.get('advisorId') ?? '';
  const from = params.get('from');
  const to = params.get('to');
  // 7.25: día o rango elegido en el calendario de tareas (null = secciones fijas).
  const range = useMemo(
    () => (from && DAY_KEY.test(from) ? { from, to: to && DAY_KEY.test(to) ? to : from } : null),
    [from, to],
  );
  const update = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch))
          if (v) next.set(k, v);
          else next.delete(k);
        return next;
      },
      { replace: true },
    );
  const setAdvisorId = (id: string) => update({ advisorId: id || null });
  const setRange = (r: { from: string; to: string } | null) =>
    update({ from: r?.from ?? null, to: r?.to ?? null });
  const { data: advisors } = useAdvisors();
  const { data, isLoading, isPlaceholderData, error } = useMyDay(advisorId || undefined, range);
  // Al cambiar de asesor o de rango la pantalla conserva lo anterior (placeholderData) mientras llega lo nuevo:
  // se atenúa y sus enlaces no responden, para no abrir los leads del asesor equivocado.
  const stale = isPlaceholderData;
  const staleClass = stale ? 'pointer-events-none opacity-60' : '';
  const { data: mySchedules } = useMySchedules();
  const mySchedule = !advisorId ? mySchedules?.items[0] : undefined;
  // La misma zona que usa la API para «hoy» (la de la empresa activa), no la del navegador.
  const tz = useCompanyTimezone();
  const todayYmd = todayInTz(tz);
  const [completing, setCompleting] = useState<MyDayTask | null>(null);
  // E1: «Confirmar asistencia» / «No asistió» de una cita, mismo diálogo que en la ficha.
  const [attending, setAttending] = useState<{
    task: MyDayTask;
    outcome: AttendanceOutcome | null;
  } | null>(null);
  const [rescheduling, setRescheduling] = useState<MyDayTask | null>(null);
  const onAttendance = (task: MyDayTask, outcome: AttendanceOutcome | null) =>
    setAttending({ task, outcome });

  const humanAdvisors = useMemo(
    () => (advisors ?? []).filter((a) => a.kind === 'HUMAN'),
    [advisors],
  );
  // Enlaces a la tabla de leads del asesor cuyo día se mira (F3): los vencidos sin tarea pendiente se trabajan ahí.
  const viewedAdvisorId = data?.advisorId ?? advisorId ?? user?.id;
  const leadsLink = (extra: Record<string, string>) =>
    `/pipeline?vista=tabla&${new URLSearchParams({
      ...(viewedAdvisorId ? { advisorId: viewedAdvisorId } : {}),
      ...extra,
    })}`;
  const vencidosLink = (band?: string) =>
    leadsLink({ followupState: 'VENCIDO', ...(band ? { overdueBand: band } : {}) });
  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Mi día</h1>
          <p className="text-muted-foreground text-sm">
            {data
              ? `${data.advisorName} · ${fmt.date(todayYmd)}${stale ? ' · cargando…' : ''}`
              : 'Cargando…'}
          </p>
          {mySchedule && (
            <p className="text-muted-foreground text-xs" data-testid="my-schedule">
              Hoy:{' '}
              {(() => {
                const cell = effectiveRanges(
                  todayYmd,
                  mySchedule.weekly,
                  mySchedule.exceptions,
                  mySchedule.overrides,
                );
                return cell.source === 'SALA'
                  ? 'usa el horario de la sala'
                  : cell.source === 'NINGUNO'
                    ? 'no atiendes'
                    : describeRanges(cell.ranges);
              })()}{' '}
              · patrón: {describeWeekly(mySchedule.weekly)}
              {mySchedules && mySchedules.items.length > 1 ? ` (${mySchedule.companyName})` : ''}
            </p>
          )}
        </div>
        {isManager && (
          <div className="w-64">
            <Select
              value={advisorId}
              onChange={(e) => setAdvisorId(e.target.value)}
              aria-label="Ver el día de"
            >
              <option value="">Mi propio día</option>
              {humanAdvisors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.fullName}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          {(error as Error).message}
        </p>
      )}

      <div
        className={`grid grid-cols-2 gap-3 lg:grid-cols-4 ${staleClass}`}
        aria-busy={stale || undefined}
      >
        {/* Con un rango del calendario activo, las secciones de vencidas y de hoy no están en pantalla. */}
        <Stat
          label="Tareas vencidas"
          value={data?.counts.overdue}
          icon={AlertTriangle}
          tone="text-danger"
          loading={isLoading}
          onClick={range ? undefined : () => scrollTo('tareas-vencidas')}
          testId="stat-tareas-vencidas"
        />
        <Stat
          label="Para hoy"
          value={data?.counts.today}
          icon={Clock}
          tone="text-primary"
          loading={isLoading}
          onClick={range ? undefined : () => scrollTo('para-hoy')}
          testId="stat-para-hoy"
        />
        <Stat
          label="Nuevos sin contactar"
          value={data?.counts.newLeads}
          icon={Inbox}
          tone="text-warning"
          loading={isLoading}
          onClick={() => scrollTo('leads-nuevos-sin-contactar')}
          testId="stat-nuevos"
        />
        <Stat
          label="Leads vencidos"
          value={data?.counts.leadsVencidos}
          icon={CalendarDays}
          tone="text-danger"
          loading={isLoading}
          to={vencidosLink()}
          testId="stat-leads-vencidos"
          sub={
            data && (
              <span className="flex flex-wrap gap-x-2" data-testid="stat-leads-vencidos-bandas">
                {(
                  [
                    ['hoy', data.counts.leadsVencidosHoy, 'HOY'],
                    ['ayer', data.counts.leadsVencidosAyer, 'AYER'],
                    ['2–7 d', data.counts.leadsVencidos2a7, 'DIAS_2_7'],
                    ['+7 d', data.counts.leadsVencidosMas7, 'MAS_7'],
                  ] as const
                ).map(([label, n, band]) => (
                  <Link
                    key={band}
                    to={vencidosLink(band)}
                    className="hover:underline"
                    title={`Leads vencidos ${label}: ver en la tabla`}
                  >
                    {label} <strong>{n}</strong>
                  </Link>
                ))}
              </span>
            )
          }
        />
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {data && (
        <div className={`space-y-6 ${staleClass}`} aria-busy={stale || undefined}>
          {data.botPriority.length > 0 && (
            <Section
              title="El bot pide que intervengas"
              count={data.botPriority.length}
              icon={Flame}
              tone="danger"
              empty=""
            >
              <ul className="grid gap-2 md:grid-cols-2">
                {data.botPriority.map((l) => (
                  <li
                    key={l.id}
                    className="bg-card flex items-center gap-3 rounded-lg border p-3"
                    data-testid="bot-priority-row"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/leads/${l.id}`} className="font-medium hover:underline">
                          {l.contactName}
                        </Link>
                        <TemperatureBadge temperature={temperatureOf(l.score)} />
                      </div>
                      <p className="text-muted-foreground truncate text-xs">
                        {l.projectName} · {l.phone ?? l.email ?? '—'}
                        {l.attention?.note ? ` · ${l.attention.note}` : ''}
                      </p>
                    </div>
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/leads/${l.id}`}>Atender</Link>
                    </Button>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          <Section
            title="Leads nuevos sin contactar"
            count={data.newLeads.length}
            total={data.counts.newLeads}
            icon={Sparkles}
            empty="Sin leads pendientes de primer contacto. 🎉"
            more={
              <>
                Se muestran los {data.newLeads.length} más antiguos de {data.counts.newLeads}.{' '}
                <Link to={leadsLink({ followupState: 'NO_GESTIONADO' })} className="underline">
                  Ver todos en la tabla
                </Link>
              </>
            }
          >
            <ul className="grid gap-2 md:grid-cols-2">
              {data.newLeads.map((l) => (
                <li
                  key={l.id}
                  className="bg-card flex items-center gap-3 rounded-lg border p-3"
                  data-testid="new-lead-row"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/leads/${l.id}`} className="font-medium hover:underline">
                        {l.contactName}
                      </Link>
                      <Badge
                        tone={l.withinSla ? 'success' : 'danger'}
                        title="Tiempo hábil desde la captación frente al plazo de primer contacto de la empresa"
                      >
                        {l.withinSla ? 'Atención a tiempo' : 'Atención tarde'} ·{' '}
                        {fmt.minutes(l.slaElapsedMinutes)}
                      </Badge>
                      {l.isRecontact && <Badge tone="muted">Recontacto</Badge>}
                      {!l.contactable && <NoContactBadge />}
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {l.projectName} · {l.medium ?? 'sin medio'} · {fmt.ago(l.capturedAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <QuickContactButtons
                      leadId={l.id}
                      phoneNormalized={l.phoneNormalized}
                      contactable={l.contactable}
                    />
                    <Button asChild size="sm">
                      <Link to={`/leads/${l.id}`}>Gestionar</Link>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      )}

      <TaskCalendar range={range} onChange={setRange} today={todayYmd} />

      {data && (
        <div className={`space-y-6 ${staleClass}`} aria-busy={stale || undefined}>
          {data.range ? (
            <Section
              title="Tareas del calendario"
              count={data.range.tasks.length}
              icon={CalendarDays}
              empty="Sin tareas en las fechas elegidas."
            >
              <p className="text-muted-foreground text-xs">
                {data.range.from === data.range.to
                  ? `Tareas del ${fmt.date(data.range.from)}`
                  : `Tareas del ${fmt.date(data.range.from)} al ${fmt.date(data.range.to)}`}
              </p>
              <ul className="space-y-2">
                {data.range.tasks.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    tone={new Date(t.scheduledAt).getTime() < Date.now() ? 'danger' : 'default'}
                    onComplete={setCompleting}
                    onAttendance={onAttendance}
                    onReschedule={setRescheduling}
                  />
                ))}
              </ul>
            </Section>
          ) : (
            <>
              <Section
                title="Tareas vencidas"
                count={data.overdue.length}
                total={data.counts.overdue}
                icon={AlertTriangle}
                tone="danger"
                empty="No tienes tareas vencidas."
                more={
                  <>
                    Se muestran las {data.overdue.length} más antiguas de {data.counts.overdue}.
                    Complétalas o reprográmalas para ver las siguientes, o{' '}
                    <Link to={vencidosLink()} className="underline">
                      revisa los leads vencidos en la tabla
                    </Link>
                    .
                  </>
                }
              >
                <ul className="space-y-2">
                  {data.overdue.map((t) => (
                    <TaskRow
                      key={t.id}
                      task={t}
                      tone="danger"
                      onComplete={setCompleting}
                      onAttendance={onAttendance}
                      onReschedule={setRescheduling}
                    />
                  ))}
                </ul>
              </Section>

              <Section
                title="Para hoy"
                count={data.today.length}
                total={data.counts.today}
                icon={Clock}
                empty="Nada programado para hoy."
                more={`Se muestran las ${data.today.length} primeras de ${data.counts.today}.`}
              >
                <ul className="space-y-2">
                  {data.today.map((t) => (
                    <TaskRow
                      key={t.id}
                      task={t}
                      tone="default"
                      onComplete={setCompleting}
                      onAttendance={onAttendance}
                      onReschedule={setRescheduling}
                    />
                  ))}
                </ul>
              </Section>

              <Section
                title="Próximos 7 días"
                count={data.upcoming.length}
                total={data.counts.upcoming}
                icon={CalendarDays}
                empty="Sin tareas próximas."
                more={`Se muestran las ${data.upcoming.length} primeras de ${data.counts.upcoming}.`}
              >
                <ul className="space-y-2">
                  {data.upcoming.map((t) => (
                    <TaskRow
                      key={t.id}
                      task={t}
                      tone="muted"
                      onComplete={setCompleting}
                      onAttendance={onAttendance}
                      onReschedule={setRescheduling}
                    />
                  ))}
                </ul>
              </Section>
            </>
          )}
        </div>
      )}

      {completing && (
        <CompleteTaskDialog
          task={completing}
          leadId={completing.lead.id}
          companyId={completing.lead.companyId}
          open
          onOpenChange={(o) => !o && setCompleting(null)}
        />
      )}
      {attending && (
        <ConfirmAppointmentDialog
          task={attending.task}
          initialOutcome={attending.outcome}
          leadId={attending.task.lead.id}
          companyId={attending.task.lead.companyId}
          open
          onOpenChange={(o) => !o && setAttending(null)}
        />
      )}
      {rescheduling && (
        <RescheduleDialog
          task={rescheduling}
          leadId={rescheduling.lead.id}
          open
          onOpenChange={(o) => !o && setRescheduling(null)}
        />
      )}
    </div>
  );
}

/**
 * Tarjeta de contador. Es clicable (F3): `to` lleva a la tabla de leads filtrada y `onClick` baja a la sección. El
 * enlace (o botón) cubre solo el rótulo y la cifra; `sub` va aparte, así puede traer sus propios enlaces (las bandas de
 * «Leads vencidos») sin anidar un <a> dentro de otro. Sin datos (error de la consulta) muestra «—», nunca un 0.
 */
function Stat({
  label,
  value,
  icon: Icon,
  tone,
  loading,
  to,
  onClick,
  sub,
  testId,
}: {
  label: string;
  value?: number | string;
  icon: typeof Clock;
  tone: string;
  loading: boolean;
  to?: string;
  onClick?: () => void;
  sub?: React.ReactNode;
  testId?: string;
}) {
  const head = (
    <>
      <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
        {label}
      </p>
      {loading ? (
        <Skeleton className="mt-1 h-7 w-16" />
      ) : (
        <p className={`text-2xl font-semibold ${tone}`} data-testid={testId}>
          {value ?? '—'}
        </p>
      )}
    </>
  );
  const link =
    'focus-visible:ring-ring block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2';
  return (
    <Card className={to || onClick ? 'hover:bg-accent/50 transition-colors' : undefined}>
      <CardContent className="flex items-center justify-between p-4">
        <div className="min-w-0">
          {to ? (
            <Link to={to} className={link} title={`${label}: ver en la tabla`}>
              {head}
            </Link>
          ) : onClick ? (
            <button
              type="button"
              onClick={onClick}
              className={`${link} w-full text-left`}
              title={`${label}: ir a la sección`}
            >
              {head}
            </button>
          ) : (
            head
          )}
          {!loading && sub && <div className="text-muted-foreground mt-1 text-xs">{sub}</div>}
        </div>
        <Icon className={`h-5 w-5 shrink-0 ${tone}`} />
      </CardContent>
    </Card>
  );
}

/** Suma días a una fecha pura AAAA-MM-DD (aritmética de calendario, sin zona). */
const plusDays = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

/**
 * 7.25: calendario de tareas: hoy, mañana, los próximos 7 días o un rango de fechas a elección. «Hoy» es el de
 * la zona de la empresa (el mismo de la cabecera y de la API) y «Próximos 7 días» es la misma ventana que la
 * sección fija (de mañana a 7 días más), para que los conteos coincidan.
 */
function TaskCalendar({
  range,
  onChange,
  today,
}: {
  range: { from: string; to: string } | null;
  onChange: (r: { from: string; to: string } | null) => void;
  /** Hoy (AAAA-MM-DD) en la zona de la empresa. */
  today: string;
}) {
  const plus = (days: number) => plusDays(today, days);
  const quick: Array<[string, { from: string; to: string }]> = [
    ['Hoy', { from: plus(0), to: plus(0) }],
    ['Mañana', { from: plus(1), to: plus(1) }],
    ['Próximos 7 días', { from: plus(1), to: plus(7) }],
  ];
  // Tope del rango (F3): como máximo MY_DAY_RANGE_MAX_DAYS días incluidos los dos extremos (unos 3 meses); la API
  // responde 400 por encima. Los inputs lo marcan con min / max y el cambio de un extremo arrastra al otro.
  const shift = (day: string, days: number) => plusDays(day, days);
  const span = MY_DAY_RANGE_MAX_DAYS - 1;
  const set = (patch: Partial<{ from: string; to: string }>) => {
    const next = { from: range?.from ?? plus(0), to: range?.to ?? plus(0), ...patch };
    if (next.to < next.from) next.to = next.from;
    if (next.to > shift(next.from, span)) {
      if (patch.from !== undefined) next.to = shift(next.from, span);
      else next.from = shift(next.to, -span);
    }
    onChange(next);
  };
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm"
      data-testid="task-calendar"
    >
      <CalendarDays className="text-muted-foreground h-4 w-4" />
      <span className="text-muted-foreground">Ver tareas de:</span>
      {quick.map(([label, r]) => (
        <Button
          key={label}
          type="button"
          size="sm"
          variant={range && range.from === r.from && range.to === r.to ? 'default' : 'outline'}
          onClick={() => onChange(r)}
        >
          {label}
        </Button>
      ))}
      <input
        type="date"
        aria-label="Desde"
        className="h-8 rounded-md border px-2 text-sm"
        value={range?.from ?? ''}
        min={range ? shift(range.to, -span) : undefined}
        onChange={(e) => e.target.value && set({ from: e.target.value })}
      />
      <span className="text-muted-foreground">a</span>
      <input
        type="date"
        aria-label="Hasta"
        className="h-8 rounded-md border px-2 text-sm"
        value={range?.to ?? ''}
        max={range ? shift(range.from, span) : undefined}
        onChange={(e) => e.target.value && set({ to: e.target.value })}
      />
      <span className="text-muted-foreground text-xs">(máximo 3 meses)</span>
      {range && (
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)}>
          Quitar
        </Button>
      )}
    </div>
  );
}

/** Sección de Mi día. `total` es el contador real: si la lista viene recortada, la insignia dice «200 de 350». */
function Section({
  title,
  count,
  total = count,
  icon: Icon,
  tone,
  empty,
  more,
  children,
}: {
  title: string;
  count: number;
  total?: number;
  icon: typeof Clock;
  tone?: 'danger';
  empty: string;
  /** Aviso bajo la lista cuando hay más de lo que se muestra. */
  more?: React.ReactNode;
  children: React.ReactNode;
}) {
  const slug = title.toLowerCase().replace(/\s+/g, '-');
  const truncated = count < total;
  return (
    <section className="scroll-mt-4 space-y-2" id={slug} data-testid={`section-${slug}`}>
      <h2
        className={`flex items-center gap-2 text-base font-semibold ${tone === 'danger' && total > 0 ? 'text-danger' : ''}`}
      >
        <Icon className="h-4 w-4" /> {title}
        <Badge tone={tone === 'danger' && total > 0 ? 'danger' : 'muted'}>
          {truncated ? `${count} de ${total}` : total}
        </Badge>
      </h2>
      {total === 0 ? (
        <p className="text-muted-foreground rounded-lg border border-dashed p-4 text-sm">{empty}</p>
      ) : (
        children
      )}
      {truncated && more && (
        <p className="text-muted-foreground text-xs" data-testid={`section-${slug}-more`}>
          {more}
        </p>
      )}
    </section>
  );
}
